import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { IExcedente } from '@model/Excedente';
import { IImportarRequest, useExcedenteService } from '@services/ExcedenteService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { ExcedenteImportarForm } from './ExcedenteImportarForm';

export type ExcedenteImportarProps = {
    modal: IModalWindow
    onExcedenteImportado: (excedente: AxiosResponse<IExcedente>) => void
}

export function ExcedenteImportarModal ({ modal, onExcedenteImportado }: ExcedenteImportarProps) {
    const [arquivo, setArquivo] = React.useState<File>();

    const loading = useLoading();
    const message = useMessage();

    const excedenteService = useExcedenteService();

    const form = useForm<IImportarRequest>({
        competencia: useField(),
        inicio: useField(null),
        termino: useField(null)
    });

    const validation = useValidation<IImportarRequest>({
        competencia: yup.string().required(),
        inicio: yup.date().required(),
        termino: yup.date().required()
    })

    const handleEnter = React.useCallback(() => {
        form.reset();
        setArquivo(undefined);
    }, [form]);

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading && arquivo !== undefined) {
            const values = form.getInputValues();

            validation.validate(values)
                .then(() => {
                    loading.start();
                    excedenteService.importar(arquivo, values)
                        .then(response => {
                            message.success("Excedente importado com sucesso");
                            modal.close();
                            onExcedenteImportado(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [arquivo, excedenteService, form, loading, message, modal, onExcedenteImportado, validation]);

    const handleArquivoChange = React.useCallback((arquivo: File) => {
        setArquivo(arquivo);
    }, []);

    return (
        <ModalWindow
            disabled={loading.isLoading}
            modal={modal}
            size="sm"
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle 
                title="Importar Excedente"
            />

            <ModalWindowContent>
                <ExcedenteImportarForm 
                    form={form}
                    onArquivoChange={handleArquivoChange}
                />
            </ModalWindowContent>

            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>Importar Excedente</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}